import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Loader2, LogOut, Save, Send, Undo2, ExternalLink } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { HeroTab } from "@/components/admin/HeroTab";
import { AccommodationsTab } from "@/components/admin/AccommodationsTab";
import { GalleryTab } from "@/components/admin/GalleryTab";
import { FeaturesTab } from "@/components/admin/FeaturesTab";
import { AccountTab } from "@/components/admin/AccountTab";

const TABS = [
  ["textos", "Textos & contatos"],
  ["video", "Vídeo & capa"],
  ["acomodacoes", "Acomodações"],
  ["galeria", "Galeria"],
  ["diferenciais", "Diferenciais"],
  ["conta", "Conta"],
];

const strip = ({ updated_at, ...rest }) => rest;

export default function AdminPanel() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("textos");
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [unpublished, setUnpublished] = useState(false);
  const [publishedAt, setPublishedAt] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/content");
    setDraft(data.draft);
    setUnpublished(data.has_unpublished);
    setPublishedAt(data.published_at);
    setDirty(false);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(formatError(e)));
  }, [load]);

  useEffect(() => {
    const h = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const update = (section, value) => {
    setDraft((d) => ({ ...d, [section]: value }));
    setDirty(true);
  };
  const setSetting = (k, v) => update("settings", { ...draft.settings, [k]: v });

  const run = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy("");
    }
  };
  const save = async () => {
    await api.put("/admin/content", strip(draft));
    setDirty(false);
    setUnpublished(true);
  };
  const onSave = () => run("save", async () => { await save(); toast.success("Rascunho salvo"); await load(); });
  const onPreview = () => run("preview", async () => { if (dirty) await save(); window.open("/preview", "_blank", "noopener"); });
  const onPublish = () => run("publish", async () => {
    if (!window.confirm("Publicar as alterações no site público?")) return;
    if (dirty) await save();
    await api.post("/admin/publish");
    toast.success("Alterações publicadas no site");
    await load();
  });
  const onDiscard = () => run("discard", async () => {
    if (!window.confirm("Descartar o rascunho e voltar para a versão publicada?")) return;
    await api.post("/admin/discard");
    toast.success("Rascunho descartado");
    await load();
  });

  if (!draft) {
    return <div className="min-h-screen bg-[#08080A] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#C5A059] animate-spin" /></div>;
  }

  const status = dirty ? "Alterações não salvas" : unpublished ? "Rascunho salvo · não publicado" : "Tudo publicado";
  const Btn = ({ name, onClick, icon: Icon, children, primary, testid }) => (
    <button type="button" onClick={onClick} disabled={!!busy} data-testid={testid} className={`inline-flex items-center gap-2 h-10 px-3 sm:px-4 text-[0.65rem] sm:text-xs uppercase tracking-[0.15em] transition-colors disabled:opacity-60 ${primary ? "bg-[#C5A059] text-[#08080A] hover:bg-[#D4AF37]" : "border border-white/15 text-white hover:border-white/50"}`}>
      {busy === name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} <span>{children}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-[#08080A] text-[#FAFAFA]" data-testid="admin-panel">
      <header className="sticky top-0 z-30 bg-[#08080A]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Gran Hotel Sé" className="h-8 w-auto" />
            <span className={`text-[0.65rem] uppercase tracking-[0.18em] px-2 py-1 border ${dirty || unpublished ? "border-amber-400/40 text-amber-300" : "border-emerald-400/40 text-emerald-300"}`} data-testid="admin-status">{status}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Btn name="save" onClick={onSave} icon={Save} testid="admin-save-draft-button">Salvar rascunho</Btn>
            <Btn name="preview" onClick={onPreview} icon={Eye} testid="admin-preview-button">Pré-visualizar</Btn>
            {unpublished && !dirty && <Btn name="discard" onClick={onDiscard} icon={Undo2} testid="admin-discard-button">Descartar</Btn>}
            <Btn name="publish" onClick={onPublish} icon={Send} primary testid="admin-publish-button">Publicar</Btn>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 self-start">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0" aria-label="Seções do painel">
            {TABS.map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`} className={`shrink-0 text-left px-4 h-11 text-xs uppercase tracking-[0.15em] border-l-2 transition-colors ${tab === id ? "border-[#C5A059] bg-white/5 text-white" : "border-transparent text-[#A1A1AA] hover:text-white"}`}>
                {label}
              </button>
            ))}
          </nav>
          <div className="hidden lg:block mt-8 pt-6 border-t border-white/10 space-y-3 text-xs text-[#71717A]">
            {publishedAt && <p>Última publicação:<br />{new Date(publishedAt).toLocaleString("pt-BR")}</p>}
            <Link to="/" target="_blank" className="flex items-center gap-2 hover:text-white" data-testid="admin-view-site-link"><ExternalLink className="w-3.5 h-3.5" /> Ver site publicado</Link>
            <button onClick={logout} className="flex items-center gap-2 hover:text-white" data-testid="admin-logout-button"><LogOut className="w-3.5 h-3.5" /> Sair</button>
          </div>
        </aside>
        <main className="min-w-0">
          {tab === "textos" && <SettingsTab settings={draft.settings} onChange={setSetting} />}
          {tab === "video" && <HeroTab settings={draft.settings} onChange={setSetting} />}
          {tab === "acomodacoes" && <AccommodationsTab items={draft.accommodations} onChange={(v) => update("accommodations", v)} />}
          {tab === "galeria" && <GalleryTab items={draft.gallery} onChange={(v) => update("gallery", v)} cover={draft.settings.hero_fallback_image} onSetCover={(u) => setSetting("hero_fallback_image", u)} />}
          {tab === "diferenciais" && <FeaturesTab items={draft.features} onChange={(v) => update("features", v)} />}
          {tab === "conta" && (
            <div className="space-y-6">
              <AccountTab />
              <button onClick={logout} className="lg:hidden h-11 px-6 text-xs uppercase tracking-[0.15em] border border-white/15" data-testid="admin-logout-button-mobile">Sair</button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
